"use client";

import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { ClientErrorBoundary } from "@/components/error/ClientErrorBoundary";
import { useSettlementMethods } from "@/hooks/useSettlementMethods";
import { useGlobalData } from "@/providers";
import { isPooledMethod } from "@/utils/settlementOwnership";
import type { FiatSettlementMethod } from "@generated/types";

import { CryptoDepositDetails } from "./crypto-deposit-details";
import { depositDetailsStyles as s } from "./deposit-details-styles";
import { FiatDepositDetails } from "./fiat-deposit-details";

interface DepositDetailsInfoProps {
  accountId: string;
}

export default function DepositDetailsInfo({
  accountId,
}: DepositDetailsInfoProps) {
  const { fiat, blockchain, isLoading } = useSettlementMethods();
  const { products } = useGlobalData();

  const tDepositDetails = useTranslations("depositDetails");

  // Find the product to determine if it's fiat or blockchain
  const product = products?.find((p) => p.code === accountId);
  const isFiat = product?.resourceType === "currencies";

  // Get settlement methods for this account, sorted: personal accounts first, pooled second
  const fiatMethods = useMemo(
    () =>
      fiat
        .filter((m) => m.currency === accountId)
        .sort((a, b) => {
          const aIsPooled = isPooledMethod(a) ? 1 : 0;
          const bIsPooled = isPooledMethod(b) ? 1 : 0;
          return aIsPooled - bIsPooled;
        }),
    [fiat, accountId]
  );

  const blockchainMethods = useMemo(
    () => blockchain.filter((m) => m.product === accountId),
    [blockchain, accountId]
  );

  if (isLoading) {
    return (
      <div className={s.loading}>
        <Loader2 className="h-8 w-8 animate-spin text-brand-boundary" />
      </div>
    );
  }

  if (isFiat) {
    if (fiatMethods.length === 0) {
      return (
        <div className={`${s.loading} text-center`}>
          {tDepositDetails("noDepositDetailsAvailable", {
            product: accountId,
          })}
        </div>
      );
    }

    return (
      <ClientErrorBoundary>
        <FiatDepositDetails
          currency={accountId}
          settlementMethods={fiatMethods as FiatSettlementMethod[]}
        />
      </ClientErrorBoundary>
    );
  } else {
    if (blockchainMethods.length === 0) {
      return (
        <div className={`${s.loading} text-center`}>
          {tDepositDetails("noDepositDetailsAvailable", {
            product: accountId,
          })}
        </div>
      );
    }

    return (
      <div className="flex flex-1 flex-col min-h-0">
        <ClientErrorBoundary>
          <CryptoDepositDetails
            product={accountId}
            settlementMethods={blockchainMethods}
          />
        </ClientErrorBoundary>
      </div>
    );
  }
}
